import React, { useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { BORDER, ChartCard, ChartSkeleton, NoData, Stat, StatStrip, TipBox, TipRow } from '../../OnlineJournal/components/analytics/chartKit';
import { fmtInt, fmtPct, share } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { plural } from '../format';
import { analyticsApi } from './analyticsApi';
import { diffDays, DOW, DOW_FULL, dowOf } from './analyticsFormat';
import { MiniToggle } from './analyticsKit';
import { HeatmapResponse, Period, ViewProps } from './analyticsTypes';

type Metric = 'created' | 'activity';
type Look = 'color' | 'numbers';

const METRICS: Record<Metric, { label: string; noun: string; color: string; subtitle: string }> = {
  created: {
    label: 'Создание документов', noun: 'Создано документов', color: '#2563eb',
    subtitle: 'Когда создают документы: сумма за период по дню недели и часу создания.'
  },
  activity: {
    label: 'Действия со статусами', noun: 'Действий со статусами', color: '#7c3aed',
    subtitle: 'Когда работают с документами: записи истории статусов за период, кроме автоматических (system).'
  }
};

const HOURS = Array.from({ length: 24 }, (_, h) => h);
/** Рабочее время для показателя «вне рабочего времени»: с 9:00 до 18:59 */
const WORK_FROM = 9;
const WORK_TO = 19;

const hh = (h: number) => String(h).padStart(2, '0');
const hourRange = (h: number) => `${hh(h)}:00–${hh(h)}:59`;

function rgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/** Насыщенность ячейки: корень, чтобы небольшие значения тоже были заметны рядом с пиком */
const alphaOf = (v: number, max: number) => (v && max ? 0.08 + 0.92 * Math.sqrt(v / max) : 0);

/** Сколько раз каждый день недели входит в период - для среднего за один такой час */
function dowCounts(period: Period): number[] {
  const counts = [0, 0, 0, 0, 0, 0, 0];
  const days = diffDays(period.from, period.to) + 1;
  const first = dowOf(period.from);
  for (let i = 0; i < days; i += 1) counts[(first + i) % 7] += 1;
  return counts;
}

const avg = (v: number, n: number) => (n ? (v / n).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) : '-');

interface Matrix {
  /** [dow][hour] по выбранной метрике и по второй - для подсказки */
  value: number[][];
  other: number[][];
  byDow: number[];
  byHour: number[];
  total: number;
  max: number;
}

function buildMatrix(data: HeatmapResponse, metric: Metric): Matrix {
  const otherKey: Metric = metric === 'created' ? 'activity' : 'created';
  const value = DOW.map(() => HOURS.map(() => 0));
  const other = DOW.map(() => HOURS.map(() => 0));
  data.cells.forEach(c => {
    if (c.dow < 0 || c.dow > 6 || c.hour < 0 || c.hour > 23) return;
    value[c.dow][c.hour] = c[metric];
    other[c.dow][c.hour] = c[otherKey];
  });
  const byDow = value.map(row => row.reduce((s, v) => s + v, 0));
  const byHour = HOURS.map(h => value.reduce((s, row) => s + row[h], 0));
  const total = byDow.reduce((s, v) => s + v, 0);
  const max = value.reduce((m, row) => Math.max(m, ...row), 0);
  return { value, other, byDow, byHour, total, max };
}

const CellTip: React.FC<{ dow: number; hour: number; m: Matrix; metric: Metric; counts: number[] }> = ({ dow, hour, m, metric, counts }) => {
  const v = m.value[dow][hour];
  const otherKey: Metric = metric === 'created' ? 'activity' : 'created';
  const rows: TipRow[] = [
    { label: METRICS[metric].noun, value: fmtInt(v), bold: true, color: METRICS[metric].color, hint: `${fmtPct(share(v, m.total))} всех за период` },
    { label: 'В среднем за один такой час', value: avg(v, counts[dow]), hint: `в периоде ${counts[dow]} ${plural(counts[dow], 'такой день', 'таких дня', 'таких дней')}` },
    { label: METRICS[otherKey].noun, value: fmtInt(m.other[dow][hour]), color: METRICS[otherKey].color, divider: true }
  ];
  return <TipBox width={270} title={`${DOW_FULL[dow]}, ${hourRange(hour)}`} rows={rows} />;
};

const TIP_SLOT = { tooltip: { sx: { bgcolor: 'transparent', p: 0, m: 0, maxWidth: 'none' } } };

const Heat: React.FC<{ data: HeatmapResponse; metric: Metric; look: Look }> = ({ data, metric, look }) => {
  const m = useMemo(() => buildMatrix(data, metric), [data, metric]);
  const counts = useMemo(() => dowCounts(data.period), [data.period]);
  const color = METRICS[metric].color;
  const maxDow = Math.max(...m.byDow);
  const maxHour = Math.max(...m.byHour);

  if (!m.total) return <NoData height={260} text="За период нет событий по выбранным фильтрам" />;

  const cols = '34px repeat(24, minmax(26px, 1fr)) 92px';
  const label = { fontSize: '0.66rem', color: '#64748b', display: 'flex', alignItems: 'center' } as const;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 820, display: 'grid', gridTemplateColumns: cols, gap: '2px' }}>
        <Box />
        {HOURS.map(h => <Box key={h} sx={{ ...label, justifyContent: 'center', pb: 0.25 }}>{hh(h)}</Box>)}
        <Box sx={{ ...label, justifyContent: 'flex-end', pb: 0.25 }}>За день</Box>

        {DOW.map((d, dow) => (
          <React.Fragment key={d}>
            <Box sx={{ ...label, fontWeight: 600, color: dow >= 5 ? '#b45309' : '#334155' }}>{d}</Box>
            {HOURS.map(h => {
              const v = m.value[dow][h];
              const a = alphaOf(v, m.max);
              return (
                <Tooltip
                  key={h} placement="top" disableInteractive enterDelay={80} componentsProps={TIP_SLOT}
                  title={<CellTip dow={dow} hour={h} m={m} metric={metric} counts={counts} />}
                >
                  <Box
                    sx={{
                      height: 28, borderRadius: '3px', bgcolor: v ? rgba(color, a) : '#f8fafc',
                      outline: v === m.max ? `1.5px solid ${color}` : undefined, outlineOffset: v === m.max ? '1px' : undefined,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.6rem', fontWeight: 600, color: a > 0.55 ? '#fff' : '#334155', cursor: 'default'
                    }}
                  >
                    {look === 'numbers' && v ? fmtInt(v) : null}
                  </Box>
                </Tooltip>
              );
            })}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.75 }}>
              <Box sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#eef2f7', overflow: 'hidden' }}>
                <Box sx={{ width: `${maxDow ? (m.byDow[dow] / maxDow) * 100 : 0}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
              </Box>
              <Typography sx={{ fontSize: '0.68rem', color: '#475569', minWidth: 34, textAlign: 'right' }} title={`${fmtPct(share(m.byDow[dow], m.total))} за период`}>
                {fmtInt(m.byDow[dow])}
              </Typography>
            </Box>
          </React.Fragment>
        ))}

        <Box sx={{ ...label, pt: 0.5 }}>Все</Box>
        {HOURS.map(h => (
          <Tooltip key={h} placement="bottom" disableInteractive componentsProps={TIP_SLOT} title={(
            <TipBox
              width={240} title={`Все дни, ${hourRange(h)}`}
              rows={[
                { label: METRICS[metric].noun, value: fmtInt(m.byHour[h]), bold: true, color, hint: `${fmtPct(share(m.byHour[h], m.total))} всех за период` },
                { label: 'В среднем за день в этот час', value: avg(m.byHour[h], counts.reduce((s, c) => s + c, 0)) }
              ]}
            />
          )}>
            <Box sx={{ height: 34, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', pt: 0.5 }}>
              <Box sx={{ width: '70%', height: `${maxHour ? Math.max(2, (m.byHour[h] / maxHour) * 26) : 0}px`, bgcolor: rgba(color, 0.55), borderRadius: '2px 2px 0 0' }} />
            </Box>
          </Tooltip>
        ))}
        <Box sx={{ ...label, justifyContent: 'flex-end', fontWeight: 700, color: '#0f172a', pt: 0.5 }}>{fmtInt(m.total)}</Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>0</Typography>
        <Box sx={{ width: 140, height: 8, borderRadius: 4, border: `1px solid ${BORDER}`, background: `linear-gradient(90deg, ${rgba(color, 0.08)}, ${rgba(color, 1)})` }} />
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{fmtInt(m.max)} за час (обведён пик)</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', ml: { sm: 'auto' } }}>
          Время - местное, по записи в базе документов. Выходные выделены цветом подписи.
        </Typography>
      </Box>
    </Box>
  );
};

/** День недели × час: когда создают документы и когда с ними работают */
const HeatmapView: React.FC<ViewProps> = ({ params, paramsKey, active, reloadToken }) => {
  const [metric, setMetric] = useState<Metric>('created');
  const [look, setLook] = useState<Look>('color');
  const load = useLoad(
    active ? (_silent: boolean, signal: AbortSignal) => analyticsApi.heatmap(params, signal) : null,
    `heatmap|${paramsKey}`, reloadToken, 'Не удалось построить активность по часам'
  );
  const data = load.data;

  const stats = useMemo<Stat[]>(() => {
    if (!data) return [];
    const m = buildMatrix(data, metric);
    if (!m.total) return [];
    let peak = { dow: 0, hour: 0, v: -1 };
    m.value.forEach((row, dow) => row.forEach((v, hour) => { if (v > peak.v) peak = { dow, hour, v }; }));
    const topDow = m.byDow.indexOf(Math.max(...m.byDow));
    const topHour = m.byHour.indexOf(Math.max(...m.byHour));
    const weekend = m.byDow[5] + m.byDow[6];
    const offHours = m.byHour.reduce((s, v, h) => s + (h < WORK_FROM || h >= WORK_TO ? v : 0), 0);
    const other = metric === 'created' ? 'activity' : 'created';
    const otherTotal = data.cells.reduce((s, c) => s + c[other], 0);
    return [
      { label: METRICS[metric].noun, value: fmtInt(m.total), hint: `${METRICS[other].noun.toLowerCase()}: ${fmtInt(otherTotal)}` },
      { label: 'Пиковый час', value: `${DOW[peak.dow]}, ${hh(peak.hour)}:00`, hint: `${fmtInt(peak.v)} · ${fmtPct(share(peak.v, m.total))}` },
      { label: 'Самый загруженный день', value: DOW_FULL[topDow], hint: `${fmtPct(share(m.byDow[topDow], m.total))} недели` },
      { label: 'Самый загруженный час', value: hourRange(topHour), hint: `${fmtPct(share(m.byHour[topHour], m.total))} суток`, tooltip: 'Час суток с наибольшим числом событий по всем дням недели' },
      { label: 'Выходные', value: fmtPct(share(weekend, m.total)), hint: `${fmtInt(weekend)} в субботу и воскресенье` },
      {
        label: 'Вне рабочего времени', value: fmtPct(share(offHours, m.total)), hint: `${fmtInt(offHours)} до ${WORK_FROM}:00 и после ${WORK_TO}:00`,
        tooltip: `Рабочим временем здесь считается ${hh(WORK_FROM)}:00–${hh(WORK_TO - 1)}:59 в любой день недели`
      }
    ];
  }, [data, metric]);

  return (
    <ChartCard
      wide title="Активность по дням недели и часам" subtitle={METRICS[metric].subtitle}
      loading={load.loading} error={load.error ? load.error.text : null} onRetry={load.retry}
      actions={(
        <>
          <MiniToggle<Metric> value={metric} onChange={setMetric} options={[{ value: 'created', label: METRICS.created.label }, { value: 'activity', label: METRICS.activity.label }]} />
          <MiniToggle<Look> value={look} onChange={setLook} options={[{ value: 'color', label: 'Цвет' }, { value: 'numbers', label: 'Числа' }]} />
        </>
      )}
    >
      {!data && !load.error && <ChartSkeleton height={340} />}
      {data && stats.length > 0 && <StatStrip items={stats} />}
      {data && <Heat data={data} metric={metric} look={look} />}
    </ChartCard>
  );
};

export default React.memo(HeatmapView);
